import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { setAuthToken } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user = accounts[0] || null;
  const loading = inProgress !== 'none';

  // AUTHCONTEXT TOKEN ACQUISITION TEMPORALMENTE DESHABILITADO PARA EVITAR BUCLES MSAL
  // useEffect(() => {
  //   const getAccessToken = async () => {
  //     if (isAuthenticated && user) {
  //       try {
  //         // Intentar con el scope de nuestra aplicación
  //         const response = await instance.acquireTokenSilent({
  //           scopes: [`${process.env.REACT_APP_AZURE_CLIENT_ID || '72a5b61a-5b4b-43d6-a5af-364cf205c37e'}/.default`],
  //           account: user,
  //         });
  //         setAuthToken(response.accessToken);
  //       } catch (error) {
  //         // Si falla, intentar con popup
  //         try {
  //           const response = await instance.acquireTokenPopup({
  //             scopes: [`${process.env.REACT_APP_AZURE_CLIENT_ID || '72a5b61a-5b4b-43d6-a5af-364cf205c37e'}/.default`],
  //             account: user,
  //           });
  //           setAuthToken(response.accessToken);
  //         } catch (popupError) {
  //           console.error('Error getting access token:', popupError);
  //           setAuthToken(null);
  //         }
  //       }
  //     } else {
  //       setAuthToken(null);
  //     }
  //   };

  //   getAccessToken();
  // }, [isAuthenticated, user, instance]);

  // AUTHCONTEXT COMPLETAMENTE DESHABILITADO - SIN TOKEN ACQUISITION PARA EVITAR BUCLES
  // useEffect(() => {
  //   if (isAuthenticated && user) {
  //     console.log('🔐 AuthContext - Usuario autenticado, configurando token básico...');
  //     const getTokenOnce = async () => {
  //       try {
  //         const response = await instance.acquireTokenSilent({
  //           scopes: [`${process.env.REACT_APP_AZURE_CLIENT_ID || '72a5b61a-5b4b-43d6-a5af-364cf205c37e'}/.default`],
  //           account: user,
  //         });
  //         setAuthToken(response.accessToken);
  //         console.log('✅ AuthContext - Token configurado una sola vez');
  //       } catch (error) {
  //         console.error('❌ AuthContext - Error obteniendo token:', error);
  //         setAuthToken(null);
  //       }
  //     };
  //     getTokenOnce();
  //   }
  // }, [isAuthenticated]);

  // Autenticación mejorada - esperar a que MSAL esté listo
  useEffect(() => {
    // Solo proceder si MSAL no está procesando (inProgress === 'none')
    if (loading) {
      console.log('⏳ AuthContext - MSAL inicializando...');
      return;
    }

    if (isAuthenticated && user) {
      console.log('🔐 AuthContext - Usuario autenticado detectado, configurando token...');
      
      const setToken = async () => {
        try {
          const response = await instance.acquireTokenSilent({
            scopes: [`${process.env.REACT_APP_AZURE_CLIENT_ID || '72a5b61a-5b4b-43d6-a5af-364cf205c37e'}/.default`],
            account: user,
          });
          setAuthToken(response.accessToken);
          console.log('✅ AuthContext - Token configurado para API');
        } catch (error) {
          console.error('❌ AuthContext - Error obteniendo token:', error);
          // Si falla el silent token, intentar reautenticar
          try {
            console.log('🔄 AuthContext - Intentando reautenticación...');
            const popupResponse = await instance.acquireTokenPopup({
              scopes: [`${process.env.REACT_APP_AZURE_CLIENT_ID || '72a5b61a-5b4b-43d6-a5af-364cf205c37e'}/.default`],
              account: user,
            });
            setAuthToken(popupResponse.accessToken);
            console.log('✅ AuthContext - Token obtenido via popup');
          } catch (popupError) {
            console.error('❌ AuthContext - Error en popup:', popupError);
            setAuthToken(null);
          }
        }
      };

      setToken();
    } else if (!loading) {
      // Solo limpiar token si MSAL no está cargando
      console.log('🚫 AuthContext - Usuario no autenticado');
      setAuthToken(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading]); // Depender también del loading state

  const login = () => {
    instance.loginPopup({
      scopes: ['User.Read'],
    }).catch((error) => {
      console.error('Login error:', error);
    });
  };

  const logout = () => {
    instance.logoutPopup().catch((error) => {
      console.error('Logout error:', error);
    });
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};